// =============================================================================
// CasinoView.ts — O Cassino da Maleta (CPI da BET), estilo abertura de caixa
// (issue #121): uma ESTEIRA horizontal de prêmios desfila em alta velocidade e
// desacelera até parar exatamente no resultado que o servidor já sorteou.
// Ninguém aposta nada: a banca sempre decide.
// GOLPE DE MESTRE (#98): quando a receita de evolução está cumprida, a esteira
// trava no prêmio garantido e o card mostra a arma suprema conquistada.
// =============================================================================
import { CASINO_PRIZES, CASINO_PRIZE_TABLE, type CasinoPrizeDef } from '../../../shared/casino';
import { CASINO_RESULT_TIME, CASINO_SPIN_TIME } from '../../../shared/constants';
import { WEAPON_TABLE } from '../../../shared/weapons';
import { abilityIcon, casinoPrizeIcon } from './pixelIcons';
import { $, $in, type Overlay } from './dom';

export interface CasinoCallbacks {
  onTick(): void;                      // cada card que cruza o marcador (som de ficha)
  onLand(prize: CasinoPrizeDef): void; // a esteira travou no prêmio
}

const ITEM_W = 92;        // card 84px + 2×4px de margem (sincronizado com o CSS)
const TARGET_INDEX = 24;  // card onde a esteira para (os 4 seguintes preenchem a borda)

// Desaceleração de tômbola: rápido no início, arrastado no fim
const easeOut = (x: number): number => 1 - Math.pow(1 - x, 3);

export class CasinoView implements Overlay {
  // Raiz única do overlay: todos os ids desta view moram dentro do #casino
  readonly root: HTMLElement = $('casino');
  private readonly q = $in(this.root);
  private timers: ReturnType<typeof setTimeout>[] = [];
  private raf = 0;

  show(prizeId: string, playerName: string, evoInto: string | null, cb: CasinoCallbacks): void {
    this.clear();
    const prize = CASINO_PRIZE_TABLE[prizeId] ?? CASINO_PRIZE_TABLE.nada;
    this.q('casino-player').textContent = `${playerName} abriu a maleta...`;
    this.q('casino-slot').classList.remove('landed', 'good', 'bad', 'evo');
    this.q('casino-desc').textContent = '';
    this.q('casino-icon').innerHTML = '';
    this.q('casino-nome').textContent = 'GIRANDO...';

    // Esteira: só prêmios sorteáveis (weight > 0) preenchem os cards, com o
    // resultado CRAVADO no card-alvo — o GOLPE DE MESTRE não roda na tômbola
    // comum, então só aparece quando É o resultado garantido
    const pool = CASINO_PRIZES.filter((p) => p.weight > 0);
    const reel: CasinoPrizeDef[] = [];
    for (let i = 0; i < TARGET_INDEX + 5; i++) {
      reel.push(pool[Math.floor(Math.random() * pool.length)]);
    }
    reel[TARGET_INDEX] = prize;

    const el = this.q('casino-reel');
    el.innerHTML = reel.map((p) =>
      `<div class="casino-card ${p.good ? 'good' : 'bad'}">${casinoPrizeIcon(p.id, 32)}<span>${p.nome}</span></div>`).join('');
    el.style.transform = 'translateX(0px)';
    this.root.style.display = '';

    // Distância até centrar o card-alvo no marcador (medida com o overlay visível)
    const viewport = el.parentElement!;
    const center = viewport.clientWidth / 2;
    const dist = TARGET_INDEX * ITEM_W + ITEM_W / 2 - center;
    const spinMs = CASINO_SPIN_TIME * 1000;
    const t0 = performance.now();
    let lastIdx = -1;
    const step = (now: number): void => {
      const t = Math.min((now - t0) / spinMs, 1);
      const off = dist * easeOut(t);
      el.style.transform = `translateX(${-off}px)`;
      // Tique de ficha a cada card que cruza o marcador (naturalmente desacelera)
      const idx = Math.floor((off + center) / ITEM_W);
      if (idx !== lastIdx) {
        lastIdx = idx;
        cb.onTick();
      }
      if (t < 1) this.raf = requestAnimationFrame(step);
      else this.land(prize, evoInto, cb, el);
    };
    this.raf = requestAnimationFrame(step);
    // O servidor descongela junto: mesmo relógio dos dois lados
    this.timers.push(setTimeout(() => this.hide(), spinMs + CASINO_RESULT_TIME * 1000));
  }

  hide(): void {
    this.clear();
    this.root.style.display = 'none';
  }

  visible(): boolean {
    return this.root.style.display !== 'none';
  }

  private land(prize: CasinoPrizeDef, evoInto: string | null, cb: CasinoCallbacks, reelEl: HTMLElement): void {
    reelEl.children[TARGET_INDEX]?.classList.add('landed');
    this.q('casino-nome').textContent = prize.nome;
    if (prize.id === 'golpe' && evoInto) {
      // O card vira o anúncio da arma suprema: ícone pixel art e descrição
      const evo = WEAPON_TABLE[evoInto];
      if (evo) {
        this.q('casino-icon').innerHTML = abilityIcon('weapon', evoInto, 40);
        this.q('casino-desc').textContent = `${evo.nome}: ${evo.desc}`;
      }
      this.q('casino-slot').classList.add('landed', 'good', 'evo');
    } else {
      this.q('casino-icon').innerHTML = casinoPrizeIcon(prize.id, 40);
      this.q('casino-desc').textContent = prize.desc;
      this.q('casino-slot').classList.add('landed', prize.good ? 'good' : 'bad');
    }
    cb.onLand(prize);
  }

  private clear(): void {
    for (const t of this.timers) clearTimeout(t);
    this.timers = [];
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
  }
}
